// Custom PDP gallery enhancements (hkskincart-2, 2026-07-07).
//
// 1) OPTIONAL ADAPTIVE HEIGHT (fixed frames use CSS) — stock Horizon stretches every carousel slide to the
//    tallest media in the gallery, so a single portrait photo forces the whole
//    gallery (cover included) into a tall letterboxed/cropped box. We size the
//    slideshow to the ACTIVE slide's own image ratio instead: a square cover
//    renders square, and the gallery only grows while a taller image is
//    actually on screen. Initial height comes from the Liquid-injected
//    `aspect-ratio` on `slideshow-slides` (product-media-gallery-content.liquid).
//
// 2) DESKTOP AFFORDANCES — the left thumbnail rail gives no clue how many
//    images exist nor that it can be scrolled. We add an image counter pill
//    ("3 / 17") on the main image and sticky up/down scroll buttons on the
//    thumbnail rail (replacing the too-subtle scroll-hint fade). CSS lives in
//    product-media-gallery-content.liquid; both are hidden on mobile.
//
// Everything is scoped to <media-gallery> (the PDP gallery) — product-card
// slideshows are untouched. Injected DOM is re-created after section morphs
// (variant changes) because ensureEnhanced() runs on every slideshow:select.

const CHEVRON_UP =
  '<svg viewBox="0 0 12 8" width="12" height="8" aria-hidden="true" focusable="false"><path d="M1 6.5 6 1.5l5 5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CHEVRON_DOWN =
  '<svg viewBox="0 0 12 8" width="12" height="8" aria-hidden="true" focusable="false"><path d="M1 1.5l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/* ---------- 1) adaptive height ---------- */

function slideRatio(slide) {
  const img = slide.querySelector('img[width][height]');
  if (!img) return null;

  const width = parseFloat(img.getAttribute('width'));
  const height = parseFloat(img.getAttribute('height'));

  return width > 0 && height > 0 ? width / height : null;
}

function applyHeight(slidesEl, slide) {
  // Fixed frames (owner's square PDP rule) remain CSS-sized on slide/variant
  // changes and resize. Keep adaptive sizing only for explicitly auto galleries.
  if (slidesEl.closest('media-gallery')?.dataset.aspectRatio !== 'adapt') {
    slidesEl.style.removeProperty('height');
    return;
  }

  const ratio = slideRatio(slide);
  if (!ratio) return;

  const width = slide.clientWidth || slidesEl.clientWidth;
  if (!width) return;

  // Sanity cap so an extreme portrait image can never eat the whole screen.
  const target = Math.min(width / ratio, window.innerHeight * 0.85);
  slidesEl.style.height = `${Math.round(target)}px`;
}

/* ---------- 2) counter pill + thumbnail rail scroll buttons ---------- */

function slideCount(slideshow) {
  return slideshow.querySelectorAll('slideshow-slides > slideshow-slide').length;
}

function currentIndex(slideshow) {
  const slides = [...slideshow.querySelectorAll('slideshow-slides > slideshow-slide')];
  const active = slides.findIndex((s) => s.getAttribute('aria-hidden') === 'false');
  return active >= 0 ? active : 0;
}

function refreshChevrons(rail) {
  const up = rail.querySelector('.mg-thumb-scroll--up');
  const down = rail.querySelector('.mg-thumb-scroll--down');
  if (!up || !down) return;

  const overflow = rail.scrollHeight - rail.clientHeight > 8;
  up.classList.toggle('mg-thumb-scroll--off', !overflow || rail.scrollTop < 8);
  down.classList.toggle(
    'mg-thumb-scroll--off',
    !overflow || rail.scrollTop + rail.clientHeight >= rail.scrollHeight - 8
  );
}

function ensureEnhanced(gallery) {
  const slideshow = gallery.querySelector('slideshow-component');
  const container = slideshow?.querySelector('slideshow-container');
  if (!slideshow || !container) return;

  // Counter pill — bottom-right of the main image (desktop only via CSS).
  if (!container.querySelector('.mg-counter') && slideCount(slideshow) > 1) {
    const pill = document.createElement('div');
    pill.className = 'mg-counter';
    pill.setAttribute('aria-hidden', 'true');
    pill.innerHTML = '<span class="mg-counter__current">1</span> / <span class="mg-counter__total">1</span>';
    container.appendChild(pill);
  }

  // Scroll buttons on the desktop thumbnail rail (vertical left/right rails
  // only — bottom-position thumbnails wrap and show everything, no scrolling).
  const rail = gallery.querySelector(
    'slideshow-controls[thumbnails]:is([pagination-position="left"], [pagination-position="right"]) .slideshow-controls__thumbnails-container'
  );
  if (rail && !rail.querySelector('.mg-thumb-scroll')) {
    rail.classList.add('mg-no-mask'); // chevrons replace the scroll-hint fade

    const make = (dir) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `mg-thumb-scroll mg-thumb-scroll--${dir} mg-thumb-scroll--off`;
      b.setAttribute('aria-label', dir === 'up' ? '向上瀏覽更多圖片' : '向下瀏覽更多圖片');
      b.innerHTML = dir === 'up' ? CHEVRON_UP : CHEVRON_DOWN;
      b.addEventListener('click', (e) => {
        e.preventDefault();
        rail.scrollBy({ top: (dir === 'up' ? -1 : 1) * rail.clientHeight * 0.8, behavior: 'smooth' });
      });
      return b;
    };

    rail.prepend(make('up'));
    rail.append(make('down'));

    rail.addEventListener('scroll', () => refreshChevrons(rail), { passive: true });
    new ResizeObserver(() => refreshChevrons(rail)).observe(rail);
    refreshChevrons(rail);
  }

  updateCounter(gallery);
}

function updateCounter(gallery, index) {
  const slideshow = gallery.querySelector('slideshow-component');
  const pill = gallery.querySelector('.mg-counter');
  if (!slideshow || !pill) return;

  const total = slideCount(slideshow);
  pill.querySelector('.mg-counter__current').textContent = (index ?? currentIndex(slideshow)) + 1;
  pill.querySelector('.mg-counter__total').textContent = total;
  pill.classList.toggle('mg-counter--off', total < 2);
}

/* ---------- wiring ---------- */

document.addEventListener('slideshow:select', (event) => {
  if (!(event.target instanceof Element)) return;
  const gallery = event.target.closest('media-gallery');
  if (!gallery) return;

  const slide = event.detail?.slide;
  const slidesEl = slide?.closest('slideshow-slides');
  if (slide && slidesEl) applyHeight(slidesEl, slide);

  // Re-create injected DOM if a variant-change morph wiped it, then sync.
  ensureEnhanced(gallery);
  if (typeof event.detail?.index === 'number') updateCounter(gallery, event.detail.index);
});

// Variant changes morph the gallery DOM and wipe the injected counter/buttons
// without necessarily firing slideshow:select — re-inject after the morph
// settles (ensureEnhanced is idempotent, so extra runs are harmless).
document.addEventListener('variant:update', () => {
  [50, 300, 900].forEach((delay) => {
    setTimeout(() => document.querySelectorAll('media-gallery').forEach(ensureEnhanced), delay);
  });
});

let resizeRaf = 0;
window.addEventListener('resize', () => {
  cancelAnimationFrame(resizeRaf);
  resizeRaf = requestAnimationFrame(() => {
    document.querySelectorAll('media-gallery slideshow-slides').forEach((slidesEl) => {
      // Until the first slide change the Liquid aspect-ratio is in charge,
      // and it is already responsive — nothing to recompute.
      if (!slidesEl.style.height) return;

      const active =
        slidesEl.querySelector('slideshow-slide[aria-hidden="false"]') ||
        slidesEl.querySelector('slideshow-slide');
      if (active) applyHeight(slidesEl, active);
    });
  });
});

document.querySelectorAll('media-gallery').forEach(ensureEnhanced);
